import React, { useState, useCallback, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { TextField, Autocomplete, Typography, Chip } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodePlaceholderAutocomplete — Placeholder insertion autocomplete.
 * Triggered by typing "$" to search/insert workflow placeholders.
 * Supports both single-value and free-text-with-placeholders modes.
 *
 * Usage:
 *   <NodePlaceholderAutocomplete
 *     label="Input Value"
 *     value={inputValue}
 *     onChange={(val) => setInputValue(val)}
 *     availablePlaceholders={[
 *       { key: 'email_subject', componentName: 'Email', type: 'text' },
 *       { key: 'form_name', componentName: 'Form', type: 'text' },
 *     ]}
 *     placeholder="Type $ to insert a placeholder"
 *   />
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: NODE_UI.fontSize.value,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    padding: '2px 4px !important',
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.text.secondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.text.primary,
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    padding: '4px 6px !important',
    fontSize: NODE_UI.fontSize.value,
  },
}));

const OptionRow = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  padding: '2px 0',
});

const OptionKey = styled('span')(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  fontWeight: 500,
  fontFamily: 'monospace',
  color: theme.palette.text.primary,
}));

const OptionSource = styled('span')(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
}));

const paperProps = {
  sx: (theme) => ({
    borderRadius: NODE_UI.borderRadiusSm,
    boxShadow: NODE_UI.shadow,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: '2px',
    '& .MuiAutocomplete-option': {
      fontSize: NODE_UI.fontSize.value,
      padding: '6px 12px',
    },
  }),
};

const NodePlaceholderAutocomplete = React.memo(function NodePlaceholderAutocomplete({
  label,
  value = '',
  onChange,
  availablePlaceholders = [],
  placeholder = 'Type $ to search placeholders',
  disabled = false,
  fullWidth = true,
  freeSolo = true,
  multiple = false,
  sx,
}) {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  // Build options from available placeholders
  const options = useMemo(() =>
    availablePlaceholders.map((p) => ({
      key: p.key || p.placeholder || p.label || '',
      componentName: p.componentName || p.source || '',
      type: p.type || 'text',
      label: `\${${p.key || p.placeholder || p.label || ''}}`,
    })),
    [availablePlaceholders],
  );

  const handleInputChange = useCallback((_e, newInputValue) => {
    setInputValue(newInputValue);
    // Open dropdown when user types "$"
    if (newInputValue.includes('$')) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, []);

  const handleChange = useCallback((_e, newValue) => {
    if (multiple) {
      // For multiple mode, newValue is an array
      const values = (newValue || []).map(v =>
        typeof v === 'string' ? v : v.label,
      );
      onChange?.(values);
    } else {
      // For single/freeSolo, insert the placeholder token
      const val = typeof newValue === 'string' ? newValue : newValue?.label || '';
      onChange?.(val);
    }
    setOpen(false);
  }, [multiple, onChange]);

  const filterOptions = useCallback((opts, { inputValue: input }) => {
    // Extract the search term after the last "$"
    const dollarIdx = input.lastIndexOf('$');
    const searchTerm = dollarIdx >= 0 ? input.slice(dollarIdx + 1).toLowerCase() : '';

    if (dollarIdx < 0) return [];

    return opts.filter((opt) =>
      opt.key.toLowerCase().includes(searchTerm) ||
      opt.componentName.toLowerCase().includes(searchTerm),
    );
  }, []);

  const renderOption = useCallback((props, option) => (
    <li {...props} key={option.key}>
      <OptionRow>
        <OptionKey>${'{'}${option.key}{'}'}</OptionKey>
        {option.componentName && (
          <OptionSource>{option.componentName}</OptionSource>
        )}
      </OptionRow>
    </li>
  ), []);

  const getOptionLabel = useCallback((option) => {
    if (typeof option === 'string') return option;
    return option.label || '';
  }, []);

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <StyledAutocomplete
        freeSolo={freeSolo}
        multiple={multiple}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        value={value}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        disabled={disabled}
        fullWidth={fullWidth}
        componentsProps={{ paper: paperProps }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder={placeholder}
          />
        )}
        renderTags={multiple ? (tagValues, getTagProps) =>
          tagValues.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={typeof option === 'string' ? option : option.key || index}
              label={typeof option === 'string' ? option : option.label}
              size="small"
              sx={{
                fontSize: NODE_UI.fontSize.caption,
                height: '22px',
                borderRadius: NODE_UI.borderRadiusSm,
              }}
            />
          )) : undefined}
        sx={sx}
      />
    </div>
  );
});

export default NodePlaceholderAutocomplete;
